import styled, { css, keyframes } from 'styled-components';

import { ANIM, CASINO_TECH, COLORS, MOBILE, SCROLLBAR } from '../../../../theme/tokens';
import { ProgressBar, Text } from '../../../../components';

// --- Deployment status hero --------------------------------------------------

/** One-shot ring that briefly highlights the hero when the owner lands on it from
 *  the home Lumio card's deep link (a soft accent halo that fades out). */
const arrivalFlash = keyframes`
  0% { box-shadow: 0 0 0 0 ${CASINO_TECH.flashRingClear}; }
  18% { box-shadow: 0 0 0 3px ${CASINO_TECH.flashRing}; }
  100% { box-shadow: 0 0 0 3px ${CASINO_TECH.flashRingClear}; }
`;

/** The bloom behind the hero is passed per-state; `background-clip: padding-box`
 *  keeps the translucent hairline crisp instead of letting the glow bleed through
 *  it (same pairing the Important-Now cards use). `scroll-margin-top` keeps it clear
 *  of the sticky header when scrolled into view from the deep link. */
export const Hero = styled.div<{ $glow: string; $flash: boolean; $clickable: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 16px;
  background: ${(p) => p.$glow};
  background-clip: padding-box;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  /* Small breathing room for the deep-linked scroll landing. */
  scroll-margin-top: 10px;

  /* On light theme the per-state glow's near-black base (GLOW_CARD_BASE) derives to
   * a muddy wash that reads as "dirty" on the white panel. Drop it for the same clean
   * surface the identifier tiles use, so the hero harmonises with the id cards below;
   * the deploy state is still carried by the dot, heading, icon and progress accent. */
  :root[data-ows-theme='light'] & {
    background: ${CASINO_TECH.tileBg};
  }

  ${(p) =>
    p.$flash &&
    css`
      animation: ${arrivalFlash} 1.3s ease-out 1;
      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

export const HeroTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

/** Soft expanding ring on the active (deploying / attention) state dot. */
const ringPulse = keyframes`
  0% { transform: scale(0.55); opacity: 0.7; }
  100% { transform: scale(1.7); opacity: 0; }
`;

export const StateDot = styled.span<{ $color: string; $pulse: boolean }>`
  position: relative;
  flex: 0 0 auto;
  margin-top: 5px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.$color};

  ${(p) =>
    p.$pulse &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: -4px;
        border-radius: 50%;
        border: 1px solid ${p.$color};
        animation: ${ringPulse} 1.8s ease-out infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        &::after {
          animation: none;
          opacity: 0;
        }
      }
    `}
`;

export const HeroHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
`;

export const HeroTitle = styled(Text)<{ $color: string }>`
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
  color: ${(p) => p.$color};
`;

export const HeroSub = styled(Text)`
  font-size: 13px;
  font-weight: 300;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

export const StateIcon = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: ${COLORS.surfaceFaint};
`;

export const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/** Reuse the shared bar; let it flex to fill the row beside the percentage. */
export const Bar = styled(ProgressBar)`
  flex: 1;
  min-width: 0;
`;

export const ProgressPct = styled(Text)<{ $color: string }>`
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.$color};
`;

export const Counts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const CountChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 99px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  font-size: 11px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  white-space: nowrap;
`;

export const CountDot = styled.span<{ $color: string }>`
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => p.$color};
`;

// --- Verbose build-step list -------------------------------------------------

export const StepsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 4px 2px;
  margin: -2px 0;
  border: none;
  background: transparent;
  color: ${COLORS.textMuted};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    color: ${COLORS.textBright};
  }
`;

export const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transition: transform ${ANIM.INTERACT_MS}ms ease;
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
`;

/** The verbose list scrolls within the hero once past ~8 steps (a long deploy can
 *  carry 24), so the panel never grows unbounded; a thin Lumio-dark scrollbar keeps
 *  it unobtrusive and `overscroll-behavior: contain` stops it scrolling the page. */
export const Steps = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-top: 1px solid ${CASINO_TECH.stepDivider};

  scrollbar-width: thin;
  scrollbar-color: ${SCROLLBAR.thumb} transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${SCROLLBAR.thumb};
    border-radius: 99px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 2px;
  border-bottom: 1px solid ${CASINO_TECH.stepDivider};

  &:last-child {
    border-bottom: none;
  }
`;

export const StepDot = styled.span<{ $color: string; $pulse: boolean }>`
  position: relative;
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};

  ${(p) =>
    p.$pulse &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: -3px;
        border-radius: 50%;
        border: 1px solid ${p.$color};
        animation: ${ringPulse} 1.8s ease-out infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        &::after {
          animation: none;
          opacity: 0;
        }
      }
    `}
`;

export const StepLabel = styled(Text)<{ $color: string }>`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: ${(p) => p.$color};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StepMeta = styled(Text)`
  flex: 0 0 auto;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.textFaint};
`;

// --- Identity grid -----------------------------------------------------------

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;

  ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

export const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${CASINO_TECH.tileBg};
  min-width: 0;
`;

export const TileLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: ${COLORS.textFaint};
`;

export const TileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const TileValue = styled(Text)`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: ${COLORS.featureName};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Monospace pill for ids / hashes / the domain, still a <Text> node so the value
 *  is auto-escaped (it can be raw API data) and never treated as HTML. */
export const Mono = styled(Text)`
  flex: 1;
  min-width: 0;
  padding: 4px 9px;
  border-radius: 7px;
  border: 1px solid ${CASINO_TECH.monoBorder};
  background: ${CASINO_TECH.monoBg};
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12.5px;
  color: ${CASINO_TECH.monoText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TileActions = styled.div`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const affordance = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${CASINO_TECH.affordance};
  cursor: pointer;
  transition:
    color ${ANIM.INTERACT_MS}ms ease,
    background ${ANIM.INTERACT_MS}ms ease;

  /* Touch targets meet the 48x48 minimum on mobile (the glyph stays the same size). */
  ${MOBILE} {
    width: 48px;
    height: 48px;
  }

  &:hover,
  &:focus-visible {
    color: ${CASINO_TECH.affordanceHover};
    background: ${COLORS.hoverWash};
  }
`;

export const CopyBtn = styled.button`
  ${affordance}
`;

export const LinkBtn = styled.a`
  ${affordance}
  text-decoration: none;
`;

// --- States ------------------------------------------------------------------

export const Empty = styled(Text)`
  display: block;
  padding: 8px 0;
  font-size: 13px;
  font-weight: 300;
  color: ${COLORS.textMuted};
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
